import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Tag from "@shared/ui/Tag";
import React, { useMemo, useRef } from "react";
import { useIntl } from "react-intl";

import { getOptionBackgroundColor } from "#/src/utils";
import { Select3Portal, Select3 } from "~/components/form/_inputs";

const CustomSelectField = ({
  field,
  handleSelectFieldSave,
  isEditable = true,
  selectFieldValueClassname = "",
  selectFieldLabelClassname = "",
  dropDownPosition = "",
  usePortal = false,
}) => {
  const { messages } = useIntl();
  const hiddenTriggerRef = useRef(null);

  const optionList = useMemo(
    () =>
      field?.options?.map((item) => ({
        label: item?.value,
        value: item?.value,
        color: item?.color,
      })) || [],
    [field?.options],
  );

  const selectedOption = optionList.find((o) => o.value === field?.value);

  const SelectComponent = usePortal ? Select3Portal : Select3;

  return (
    <div className="flex items-center gap-sm py-sm custom-field-row">
      <BodyText
        size={BODY_TEXT_SIZES.X_SMALL}
        color="text-secondary"
        title={field?.label}
        className={`w-[140px] flex-shrink-0 overflow-hidden text-ellipsis whitespace-nowrap ${selectFieldLabelClassname}`}
      >
        {field?.label}:
      </BodyText>

      <div className="flex-1 min-w-0 relative mb-1.5">
        {/* Visible display — clicking opens the hidden Select3 dropdown */}
        <div
          className={isEditable ? "cursor-pointer inline-flex" : "inline-flex"}
          onClick={() => {
            if (isEditable && hiddenTriggerRef.current) {
              hiddenTriggerRef.current.querySelector("button")?.click();
            }
          }}
        >
          {selectedOption ? (
            <div className="pl-1.5">
              <Tag
                label={selectedOption.label}
                isHoverable={false}
                style={{
                  color: selectedOption.color,
                  background: getOptionBackgroundColor(selectedOption.color),
                }}
              />
            </div>
          ) : (
            <BodyText
              size={BODY_TEXT_SIZES.X_SMALL}
              color="text-secondary pl-1.5"
            >
              {messages?.settings?.companyTabs?.customizeFields?.empty || "-"}
            </BodyText>
          )}
        </div>

        {/* Hidden Select3 — only the dropdown popup is visible when open */}
        <div
          ref={hiddenTriggerRef}
          style={{
            position: "absolute",
            height: 0,
            width: 0,
            overflow: "visible",
            top: 0,
            left: 0,
          }}
        >
          <SelectComponent
            disabled={!isEditable}
            buttonModifier="c-dropdown-button--uppercase !invisible !absolute"
            search={true}
            position={dropDownPosition}
            dropdownTitle={`${field?.label}:`}
            customColor={true}
            name={field?.label}
            label=""
            placeholder={
              messages?.settings?.companyTabs?.customizeFields?.empty
            }
            defaultValue={field?.value || ""}
            options={optionList}
            getValues={() =>
              field?.options?.find((item) => field?.value === item?.value)
                ?.value || ""
            }
            onChange={(val) => handleSelectFieldSave(field?._id, val)}
            valueClassname={selectFieldValueClassname}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomSelectField;
